import React, { FunctionComponent } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { Box, Stack, Text } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import { useAppUpdatesContext } from '@app/app';
import SentryLogger from '@app/sentry';
import { AppUpdatesTable } from '@app/app-updates-table';
import { AppUpdatesFilters } from '@app/app-updates-table-filters';
import { useAnalyticsEvent, appUpdatesUIEvents } from '@app/analytics';
import { getProductType, getProductConfig } from '@app/app-updates-product-app';
import type { UiProductType, ProductKey } from '@app/app-updates-product-app';
import { BetaBanner } from './components/BetaBanner';
import { UpdatesResponseSchema } from './schema';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';

interface UpdateFilterParams {
  search?: string;
  products?: string;
  statuses?: string;
  types?: string;
}

type UpdateQueryParams =
  | { cursor: string }
  | (UpdateFilterParams & { limit: number });

const messages = defineMessages({
  listPageTitle: {
    id: 'app.updates.title',
    defaultMessage: "What's new across Atlassian",
    description: 'Title for the app updates list page',
  },
  listPageSubtitle: {
    id: 'app.updates.subtitle',
    defaultMessage:
      "Discover new cloud features designed to unleash your team's potential and see how our latest updates can power your work today.",
    description: 'Subtitle describing the app updates list page content',
  },
  browserTabTitle: {
    id: 'app.updates.browserTabTitle',
    defaultMessage: 'Release Notes | Atlassian Community',
    description: 'Browser tab title for the release notes list page',
  },
});

const AppUpdatesListContent: FunctionComponent = () => {
  const { api, dispatch, state } = useAppUpdatesContext();
  const { formatMessage } = useIntl();
  const { sendScreenEvent } = useAnalyticsEvent();

  React.useEffect(() => {
    sendScreenEvent(appUpdatesUIEvents.screen.appUpdatesListScreen.data);
  }, [sendScreenEvent]);

  const { committedFilters, pagination } = state.updates;
  const {
    selectedProducts: committedProducts,
    selectedStatuses: committedStatuses,
    selectedChangeTypes: committedChangeTypes,
    searchQuery: committedSearchQuery,
    committedFiltersVersion,
  } = committedFilters;

  // Convert UiProductType ids (e.g. 'JIRA_SOFTWARE') to ProductKey (e.g. 'jira-software')
  // for the backend API, which expects kebab-case ProductKey format.
  const selectedProducts =
    committedProducts
      ?.map(({ id }) => getProductConfig(id as UiProductType)?.productKey)
      .filter((key): key is ProductKey => key !== undefined)
      .join(',') || undefined;
  const selectedStatuses =
    committedStatuses?.map(({ id }) => id).join(',') || undefined;
  const selectedChangeTypes =
    committedChangeTypes?.map(({ id }) => id).join(',') || undefined;
  const searchQuery = committedSearchQuery || undefined;

  const updatesQuery = useQuery({
    queryKey: [
      '/gateway/api/public/app-updates/v1/changes',
      pagination.cursor ?? null,
      pagination.limit,
      searchQuery ?? null,
      selectedProducts ?? null,
      selectedStatuses ?? null,
      selectedChangeTypes ?? null,
      // This fixes scenario where user clicks on Apply but do not change filters,
      // in which case the query would not refetch because queryKey did not change.
      // With this, even if filters did not change, the query will refetch because
      // committedFiltersVersion increments on every Apply click.
      committedFiltersVersion,
    ],
    // Shows old data while fetching new data, to prevent showing empty state with spinner
    // on every filter change.
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      try {
        if (
          typeof window !== 'undefined' &&
          typeof window.scrollTo === 'function'
        ) {
          window.scrollTo({ top: 0, left: 0 });
        }

        const params: UpdateQueryParams = pagination.cursor
          ? { cursor: pagination.cursor }
          : {
              limit: pagination.limit,
              search: searchQuery,
              products: selectedProducts,
              statuses: selectedStatuses,
              types: selectedChangeTypes,
            };

        const response = await api.get(
          '/gateway/api/public/app-updates/v1/changes',
          {
            params,
          },
        );

        const parsedResponse = UpdatesResponseSchema.parse(response.data);
        return {
          updates: [
            ...(parsedResponse?.data.map((update) => ({
              ...update,
              productTypes:
                update.products
                  ?.map((product) => getProductType(product))
                  .filter(
                    (product: UiProductType | null): product is UiProductType =>
                      product != null,
                  ) || [],
            })) || []),
          ],
          links: parsedResponse?.links,
        };
      } catch (error) {
        // Distinguish Zod schema parse failures (malformed API response) from
        // network/HTTP errors so Sentry gives clearer diagnostics.
        const isParseError =
          error instanceof Error && error.name === 'ZodError';
        SentryLogger.error(error, {
          extra: {
            action: isParseError
              ? 'parse-updates-list-response'
              : 'fetch-updates-list',
          },
        });
        throw error;
      }
    },
  });

  React.useEffect(() => {
    dispatch({
      type: 'UPDATES_LOADING_SET',
      payload: {
        isLoading: updatesQuery.isFetching,
      },
    });
  }, [dispatch, updatesQuery.isFetching]);

  React.useEffect(() => {
    dispatch({
      type: 'UPDATES_ERROR_SET',
      payload: {
        error: updatesQuery.error?.message || null,
      },
    });
  }, [dispatch, updatesQuery.error]);

  React.useEffect(() => {
    dispatch({
      type: 'UPDATES_DATA_LINKS_SET',
      payload: {
        updates: updatesQuery.data?.updates || [],
        links: updatesQuery.data?.links
          ? updatesQuery.data.links
          : { self: '', next: null, prev: null },
      },
    });
  }, [dispatch, updatesQuery.data, updatesQuery.isLoading]);

  // UFOSegment provides the interaction context required by UFOLoadHold.
  // UFOLoadHold holds the page-load interaction open while data is fetching;
  // when isFetching becomes false, react-ufo automatically completes and
  // sends the operational event to the analytics pipeline.
  return (
    <UFOSegment name="app-updates">
      <UFOLoadHold name="updates-data" hold={updatesQuery.isFetching} />
      <Box as="main" paddingBlockStart="space.300">
        <Helmet>
          <title>{formatMessage(messages.browserTabTitle)}</title>
        </Helmet>
        <Stack space="space.400">
          <Stack space="space.100">
            <Heading size="xxlarge">
              {formatMessage(messages.listPageTitle)}
            </Heading>
            <Text color="color.text.subtlest">
              {formatMessage(messages.listPageSubtitle)}
            </Text>
          </Stack>
          <BetaBanner />
          <AppUpdatesFilters />
          <AppUpdatesTable />
        </Stack>
      </Box>
    </UFOSegment>
  );
};

export const AppUpdatesListPage: FunctionComponent = () => {
  return <AppUpdatesListContent />;
};

export default AppUpdatesListPage;
